/**
 * Service Worker 注册与更新编排（§11.11）。
 *
 * 红线 #57：牌局中途绝不触发 SW 更新刷新。
 * 检测到新版本只记下来，等到空闲时机（判据见 updateIdle.ts）才 reload；
 * 30 分钟内等不到空闲，就退化成「只有用户主动点才更新」。等待期间界面上一直有
 * 「立即更新」入口（UpdateNotice），用户点了就立刻刷新 —— 那是用户自己的决定，不是自动触发。
 */

import { registerSW } from 'virtual:pwa-register'

/** 等待空闲时机的最长时间；超过就不再自动刷新，改为用户主动触发 */
export const AUTO_UPDATE_WINDOW_MS = 30 * 60_000
/** 用户点了「立即更新」后，新 SW 迟迟不接管时的兜底刷新 */
export const FORCE_RELOAD_FALLBACK_MS = 4_000

export interface UpdateGate {
  /** 当前是否处于可以安全刷新的空闲时刻 */
  isIdle(): boolean
  /** 有新版本待安装时回调，用于弹 Toast */
  onWaiting?: () => void
  /** 真的要刷新前回调（保存草稿之类） */
  onBeforeReload?: () => void
}

export interface PwaController {
  /** 是否有新版本在等 */
  hasUpdate(): boolean
  /** 用户主动点「立即更新」 */
  applyUpdate(): void
  dispose(): void
}

const noop = (): void => {}

export function setupPwa(gate: UpdateGate): PwaController {
  let waiting = false
  let detectedAt = 0
  let timer: ReturnType<typeof setInterval> | undefined

  const doReload = (): void => {
    ;(gate.onBeforeReload ?? noop)()
    void updateSW(true)
  }

  /**
   * 用户点「立即更新」：同一条路，外加一个兜底刷新。
   * `updateSW(true)` 靠新 SW 接管（controllerchange）才刷新；某些 WebView 上这个事件不来，
   * 按钮就停在「更新中…」。几秒后直接刷新：跳过等待的消息已经发出，新 SW 会接管这次加载。
   */
  const forceReload = (): void => {
    doReload()
    setTimeout(() => globalThis.location?.reload(), FORCE_RELOAD_FALLBACK_MS)
  }

  const updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      waiting = true
      detectedAt = Date.now()
      ;(gate.onWaiting ?? noop)()
      timer ??= setInterval(() => {
        if (!waiting) return
        if (Date.now() - detectedAt > AUTO_UPDATE_WINDOW_MS) {
          // 超窗：停掉轮询，只保留手动入口
          clearInterval(timer)
          timer = undefined
          return
        }
        if (gate.isIdle()) {
          clearInterval(timer)
          timer = undefined
          doReload()
        }
      }, 5_000)
    },
  })

  return {
    hasUpdate: () => waiting,
    applyUpdate: () => {
      if (waiting) forceReload()
    },
    dispose: () => {
      if (timer !== undefined) clearInterval(timer)
      timer = undefined
    },
  }
}
